export interface AuthHint {
  authenticated: true;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePicture?: string;
  idTokenHint?: string;
  updatedAt: number;
}

// Navbar-only hint. This is not an auth session and never gates protected
// routes; it only prevents public-header flicker while Keycloak catches up.
const AUTH_HINT_KEY = String.fromCharCode(95, 95, 97, 103, 116, 95, 117, 105);
const LEGACY_AUTH_HINT_KEYS = ['agentous_auth_hint'];
const AUTH_HINT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const AUTH_HINT_VERSION = 1;
const AUTH_HINT_CODEC_KEY = 'agentous-public-auth-ui-v1';
const PROBE_KEY = String.fromCharCode(
  95,
  95,
  97,
  103,
  116,
  95,
  112,
  114,
  111,
  98,
  101
);

const getWritableStorages = () => {
  if (typeof window === 'undefined') return [];

  const candidates: Storage[] = [];

  try {
    candidates.push(window.localStorage);
  } catch {
    // Safari privacy modes can throw when storage is accessed.
  }

  try {
    candidates.push(window.sessionStorage);
  } catch {
    // Ignore unavailable storage and use any remaining option.
  }

  return candidates.filter(storage => {
    try {
      storage.setItem(PROBE_KEY, '1');
      storage.removeItem(PROBE_KEY);
      return true;
    } catch {
      return false;
    }
  });
};

const scrubLegacyHints = (storage: Storage) => {
  LEGACY_AUTH_HINT_KEYS.forEach(key => storage.removeItem(key));
};

// Keycloak stores temporary callback states as kc-callback-* keys. Interrupted
// silent checks can leave them behind, especially in Safari, so public pages
// prune old entries when no active callback is being processed.
const isActiveKeycloakCallback = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('state') && (params.has('code') || params.has('error'));
};

export const clearStaleKeycloakCallbacks = () => {
  if (isActiveKeycloakCallback()) return;

  getWritableStorages().forEach(storage => {
    const keys = Array.from({ length: storage.length }, (_, index) =>
      storage.key(index)
    );

    keys.forEach(key => {
      if (key?.startsWith('kc-callback-')) {
        storage.removeItem(key);
      }
    });
  });
};

const toBase64 = (value: string) => {
  const bytes = new TextEncoder().encode(value);
  const binary = Array.from(bytes, byte => String.fromCharCode(byte)).join('');
  return window.btoa(binary);
};

const fromBase64 = (value: string) => {
  const binary = window.atob(value);
  const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
};

const codec = (value: string) => {
  let result = '';

  for (let index = 0; index < value.length; index += 1) {
    const keyCode = AUTH_HINT_CODEC_KEY.charCodeAt(
      index % AUTH_HINT_CODEC_KEY.length
    );
    result += String.fromCharCode(value.charCodeAt(index) ^ keyCode);
  }

  return result;
};

const encodeHint = (hint: AuthHint) => {
  const payload = JSON.stringify({
    v: AUTH_HINT_VERSION,
    a: hint.authenticated ? 1 : 0,
    f: hint.firstName,
    l: hint.lastName,
    e: hint.email,
    p: hint.profilePicture,
    i: hint.idTokenHint,
    t: hint.updatedAt,
  });

  return toBase64(codec(toBase64(payload)));
};

const decodeHint = (value: string): AuthHint | null => {
  const parsed = JSON.parse(fromBase64(codec(fromBase64(value)))) as {
    v?: number;
    a?: number;
    f?: string;
    l?: string;
    e?: string;
    p?: string;
    i?: string;
    t?: number;
  };

  if (parsed.v !== AUTH_HINT_VERSION || parsed.a !== 1 || !parsed.t) {
    return null;
  }

  return {
    authenticated: true,
    firstName: parsed.f,
    lastName: parsed.l,
    email: parsed.e,
    profilePicture: parsed.p,
    idTokenHint: parsed.i,
    updatedAt: parsed.t,
  };
};

export const getAuthHint = (): AuthHint | null => {
  const storages = getWritableStorages();
  if (!storages.length) return null;

  for (const storage of storages) {
    try {
      scrubLegacyHints(storage);
      const raw = storage.getItem(AUTH_HINT_KEY);
      if (!raw) continue;

      const parsed = decodeHint(raw);
      if (!parsed) {
        storage.removeItem(AUTH_HINT_KEY);
        continue;
      }

      if (Date.now() - parsed.updatedAt > AUTH_HINT_TTL_MS) {
        storage.removeItem(AUTH_HINT_KEY);
        continue;
      }

      return parsed;
    } catch {
      storage.removeItem(AUTH_HINT_KEY);
    }
  }

  return null;
};

export const setAuthHint = (
  hint: Omit<AuthHint, 'authenticated' | 'updatedAt'> = {}
) => {
  const storages = getWritableStorages();
  if (!storages.length) return;
  const existingHint = getAuthHint();

  const nextHint: AuthHint = {
    authenticated: true,
    firstName: hint.firstName ?? existingHint?.firstName,
    lastName: hint.lastName ?? existingHint?.lastName,
    email: hint.email ?? existingHint?.email,
    profilePicture: hint.profilePicture ?? existingHint?.profilePicture,
    idTokenHint: hint.idTokenHint ?? existingHint?.idTokenHint,
    updatedAt: Date.now(),
  };

  storages.forEach(storage => {
    scrubLegacyHints(storage);
    storage.setItem(AUTH_HINT_KEY, encodeHint(nextHint));
  });
};

export const clearAuthHint = () => {
  const storages = getWritableStorages();

  storages.forEach(storage => {
    storage.removeItem(AUTH_HINT_KEY);
    scrubLegacyHints(storage);
  });
};
